import { Component, OnInit, OnDestroy } from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import BusinessEntity from 'src/app/services/businessEntity.model';
import { BusinessEntityService } from 'src/app/services/businessEntity.service';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import CultureEstimatorModel from 'src/app/services/cultureEstimator.model';
import { CultureEstimatorService } from 'src/app/services/cultureEstimator.service';
import { ExcelService } from 'src/app/services/excelService';
import FarmSiteModel from 'src/app/services/farmSite.model';
import { FarmSiteService } from 'src/app/services/farmSite.service';
import PersonsModel from 'src/app/services/persons.model';
import { PersonsService } from 'src/app/services/persons.service';
import PondSectionModel from 'src/app/services/pondSection.model';
import { PondSectionService } from 'src/app/services/pondSection.service';
import { environment } from 'src/environments/environment';
import * as _ from 'lodash';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

/**
 * CultureEstimatorComponent
 * 
 * A reporting and estimation component that calculates aquaculture culture parameters
 * and provides estimates for farm sites and pond sections. Allows users to select
 * business, farm site, and section to view current and estimated culture data.
 * 
 * Key responsibilities:
 * - Provide hierarchical selection interface (business → farm site → section)
 * - Load and filter business entities based on user role
 * - Retrieve farm sites and pond sections dynamically
 * - Calculate culture estimations for selected sections
 * - Display current vs. estimated culture parameters
 * - Export culture estimation data to Excel
 * - Support fullscreen view for detailed analysis
 * 
 * User roles supported:
 * - Farmer: Can view only their own farm business
 * - Farm Manager: Can view only assigned farm business
 * - Business Admin (Farm): Can view all farms in their business
 * - Business Admin (Provider): Can view all farmer businesses
 * - Provider Manager: Can view farms assigned to their technicians
 * 
 * Features:
 * - Dynamic business dropdown filtering
 * - Auto-selection of single option (farm site, section)
 * - Real-time form validation
 * - Fullscreen view for culture data visualization
 * - Excel export functionality for estimation reports
 * - Loading indicators and error handling
 * 
 * Data flow:
 * 1. Load businesses based on user role and business category
 * 2. User selects business from dropdown
 * 3. Load farm sites for selected business
 * 4. Auto-select farm site if only one available
 * 5. Load pond sections for selected farm site
 * 6. Auto-select section if only one available
 * 7. Submit to generate culture estimations
 * 8. Display results with export option
 * 
 * localStorage keys used:
 * - loginPersonType: User role (Farmer, Farm Manager, Business Admin, etc.)
 * - loginPersonName: User display name
 * - personID: Unique person identifier
 * - loginPersonMobileNumber: User contact number
 * - loginBusinessCategory: Business type (Farm, Provider, Others)
 * - loginBusinessName: Business name
 * - businessID: Business identifier
 * - LoginUserName: User full name for reports
 * 
 * @component
 * @selector app-culture-estimator
 * @example
 * <app-culture-estimator></app-culture-estimator>
 * 
 * @implements {OnInit, OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-culture-estimator',
  templateUrl: './culture-estimator.component.html',
  styleUrls: ['./culture-estimator.component.scss']
})
export class CultureEstimatorComponent implements OnInit, OnDestroy {

  // ============ Models related Properties ============
  /** 
   * Array of business entities (farms/providers)
   * @type {Array<BusinessEntity>}
   */
  public businesses: Array<BusinessEntity>;
  
  /** 
   * Currently selected business entity model
   * @type {BusinessEntity}
   */
  public businessModel: BusinessEntity;
  
  /** 
   * Array of farm sites for selected business
   * @type {Array<FarmSiteModel>}
   */
  public farmsites: Array<FarmSiteModel>;
  
  /** 
   * Currently selected farm site model
   * @type {FarmSiteModel}
   */
  public farmsiteModel: FarmSiteModel;
  
  /** 
   * Array of pond sections for selected farm site
   * @type {Array<PondSectionModel>}
   */
  public sections: Array<PondSectionModel>;
  
  /** 
   * Currently selected pond section model
   * @type {PondSectionModel}
   */
  public sectionModel: PondSectionModel;
  
  /** 
   * Culture estimator data containing current and estimated parameters
   * @type {CultureEstimatorModel}
   */
  public cultureEstimatorsData: CultureEstimatorModel;
  
  /** 
   * Duplicate copy of culture estimator data for display purposes
   * @type {CultureEstimatorModel}
   */
  public dupCultureEstimatorsData: CultureEstimatorModel;
  
  /** 
   * Array of persons/technicians associated with business
   * @type {Array<PersonsModel>}
   */
  public persons: Array<PersonsModel>;
  
  // ============ Reactive Form Validation Properties ============
  /** 
   * Reactive form group for culture estimator selection form
   * Controls: businessfield, locationField, sectionField
   * @type {FormGroup}
   */
  public registerForm: FormGroup;
  
  /** 
   * Flag indicating if form has been submitted
   * Used for validation error display
   * @type {boolean}
   * @default false
   */
  public formSubmitted: boolean = false;

  // ============ Functionality variables ============
  /** 
   * Loading indicator for data retrieval operations
   * Disables submit button while loading
   * @type {boolean}
   * @default false
   */
  public enableDisableDataLoader: boolean = false;
  
  /** 
   * Search/filter text for business selection
   * @type {string}
   */
  public search: string = '';
  
  /** 
   * Flag indicating if no data is available for display
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;
  
  /** 
   * Current page number for pagination (if applicable)
   * @type {number}
   */
  public activePageNo: number;

  // ============ Login Credentials ============
  /** 
   * Person type of logged-in user (Farmer, Farm Manager, Business Admin, etc.)
   * Retrieved from localStorage
   * @type {string}
   */
  public loginPersonType: string;
  
  /** 
   * Business category of logged-in user (Farm, Provider, Others)
   * Retrieved from localStorage
   * @type {string}
   */
  public loginBusinessCategory: string;
  
  /** 
   * Business name of logged-in user
   * Retrieved from localStorage
   * @type {string}
   */
  public loginBusinessName: string;
  
  /** 
   * Full name of logged-in person
   * Retrieved from localStorage
   * @type {string}
   */
  public loginPersonName: string;
  
  /** 
   * Mobile number of logged-in person
   * Retrieved from localStorage
   * @type {string}
   */
  public loginPersonMobileNumber: string;
  
  /** 
   * Unique identifier of logged-in person
   * Retrieved from localStorage
   * @type {string}
   */
  public loginPersonID: string;
  
  // ============ Customized Business dropdown Properties ============
  /** 
   * Flag for controlling business dropdown open/close state
   * @type {boolean}
   * @default false
   */
  public isDropdownOpen = false;
  
  /** 
   * Filtered list of business options for dropdown display
   * Updated as user types in search field
   * @type {Array<any>}
   */
  public filteredOptions: any[] = [];
  
  /** 
   * Placeholder text for business dropdown search field
   * @type {string}
   * @default 'Enter the business name'
   */
  public businessSearch: string = 'Enter the business name';
  
  /** 
   * Flag to disable submit button during form submission
   * @type {boolean}
   */
  public submitButtonDisabled: boolean;
  
  /** 
   * Name of exported Excel file
   * @type {string}
   */
  public file_name: string;
  
  /** 
   * Flag indicating if submit button was clicked
   * Used to show/hide results
   * @type {boolean}
   */
  public isSubmitClicked: boolean;

  /**
   * Constructor for CultureEstimatorComponent
   * 
   * Initializes the component with required services and creates the selection form
   * with validation for business, farm site, and section fields.
   * 
   * Form validation rules:
   * - businessfield: Required
   * - locationField: Required (farm site)
   * - sectionField: Required (pond section)
   * 
   * Services injected:
   * - BusinessEntityService: Manages farm/provider business entities
   * - FarmSiteService: Retrieves farm site information
   * - PondSectionService: Retrieves pond section information
   * - FormBuilder: Creates reactive form group
   * - ToastrService: Displays toast notifications
   * - ExcelService: Handles Excel export operations
   * - CommonMethodsService: Common utility methods
   * - CultureEstimatorService: Culture estimation calculations
   * - PersonsService: Retrieves technician and person information
   * - LocationStrategy: Current URL path
   * - PreviousRouteService: Navigation history tracking
   * 
   * @constructor
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations
   * @service BusinessEntityService
   * @param {FarmSiteService} farmSiteService - Service for farm site operations
   * @service FarmSiteService
   * @param {PondSectionService} sectionService - Service for pond section operations
   * @service PondSectionService
   * @param {FormBuilder} formBuilder - Angular form builder
   * @service FormBuilder
   * @param {ToastrService} toastrService - Toast notification service
   * @service ToastrService
   * @param {ExcelService} excelService - Excel export service
   * @service ExcelService
   * @param {CommonMethodsService} commonMethodsService - Common utility methods
   * @service CommonMethodsService
   * @param {CultureEstimatorService} cultureEstimatorService - Culture estimation service
   * @service CultureEstimatorService
   * @param {PersonsService} personService - Service for person/technician operations
   * @service PersonsService
   * @param {LocationStrategy} locationStrategy - Browser location strategy
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Navigation history service
   * @service PreviousRouteService
   */
  constructor(public businessEntityService: BusinessEntityService, public farmSiteService: FarmSiteService, public sectionService: PondSectionService, public formBuilder: FormBuilder,
              public toastrService: ToastrService, public excelService: ExcelService, public commonMethodsService: CommonMethodsService,
              public cultureEstimatorService: CultureEstimatorService, public personService: PersonsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.businessModel = new BusinessEntity();
    this.farmsiteModel = new FarmSiteModel();
    this.sectionModel = new PondSectionModel();
    this.registerForm = this.formBuilder.group({
      businessfield: ['', Validators.required],
      locationField: ['', Validators.required],
      sectionField: ['', Validators.required],
    });
  }

  /**
   * Angular component initialization lifecycle hook
   * 
   * Performs initial setup of component:
   * 1. Retrieves login credentials from localStorage
   * 2. Tracks current route in navigation history
   * 3. Loads business entities based on user role:
   *    - Farmers/Farm Managers: Auto-selects their business and loads farm sites
   *    - Business Admin (Farm): Lists all farms in their business
   *    - Business Admin (Provider): Lists all farmer businesses
   *    - Provider Managers: Lists farms assigned to their technicians
   * 4. Auto-selects single available options (farm site, section)
   * 
   * User role logic:
   * - Farmer, Farm Manager, Business Admin (Farm): Direct business access
   * - Business Admin (Provider): Get all farmer businesses excluding themselves
   * - Provider Manager: Get businesses from assigned technicians
   * 
   * localStorage keys accessed:
   * - loginPersonType: User role
   * - loginPersonName: Display name
   * - personID: Person identifier
   * - loginPersonMobileNumber: Contact number
   * - loginBusinessCategory: Business category
   * - loginBusinessName: Business name
   * - businessID: Business identifier
   * 
   * Error handling:
   * - Catches all errors and displays user-friendly messages
   * - Continues initialization even if data fetch fails
   * 
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook - Called once after component creation
   * @throws {Error} If service calls fail
   */
  public async ngOnInit(): Promise<void> {
    this.loginPersonType = localStorage.getItem('loginPersonType');
    this.loginPersonName = localStorage.getItem('loginPersonName');
    this.loginPersonID = localStorage.getItem('personID');
    this.loginPersonMobileNumber = localStorage.getItem('loginPersonMobileNumber');
    this.loginBusinessCategory = localStorage.getItem('loginBusinessCategory');
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      if (['Farmer', 'Farm Manager', 'Business Admin'].includes(this.loginPersonType) && this.loginBusinessCategory == 'Farm') {
        this.loginBusinessName = localStorage.getItem('loginBusinessName');
        this.registerForm.get('businessfield').patchValue(this.loginBusinessName);
        this.getFarmSitesByBusinessID(localStorage.businessID);
      } else {
        this.businessSearch = undefined;
        this.registerForm.get('businessfield').patchValue('');
        if (this.loginPersonType == 'Business Admin') {
          const businessEntities = await this.businessEntityService.getAllfarmerByProviderID(localStorage.businessID);
          this.businesses = businessEntities && businessEntities.length > 0 ? businessEntities.filter((businessObject: any) => businessObject.id != localStorage.businessID) : []
          this.filteredOptions = this.businesses;
        } else {
          this.persons = await this.personService.getAllTechniciansByProviderManagerId(this.loginPersonID);
          let technicianIds = this.persons.length > 0 ? _.map(this.persons, 'id') : [];
          if (technicianIds.length > 0) {
            let technicianFarmsFarmsites = await this.farmSiteService.getFarmeSitesByTechnicianIDs(technicianIds);
            if (technicianFarmsFarmsites && technicianFarmsFarmsites.length > 0) {
              this.businesses = _.uniqBy(_.map(technicianFarmsFarmsites, 'businessID'), 'id');
              this.filteredOptions = this.businesses;
            }
          }
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Filters business options based on search text
   * 
   * Implements real-time search/filter functionality for the business dropdown.
   * Compares user input against business names to display matching options.
   * 
   * Filtering logic:
   * 1. Retrieves current value from businessfield form control
   * 2. Converts value to string if it's a BusinessEntity object or string
   * 3. Extracts business name and converts to lowercase
   * 4. Filters businesses array by comparing lowercase names
   * 5. Updates filteredOptions with matching results
   * 
   * Input types:
   * - string: Search text entered by user
   * - BusinessEntity: Selected object from dropdown
   * 
   * Case-insensitive matching:
   * - User input converted to lowercase
   * - Business names converted to lowercase
   * - Partial matches included (contains, not exact)
   * 
   * @method filterBusinesses
   * @returns {void}
   * @side-effect Updates filteredOptions array with matching businesses
   * 
   * @example
   * // In template: (keyup)="filterBusinesses()"
   * this.filterBusinesses(); // Called on dropdown input change
   */
  public filterBusinesses(): void {
    const value = (this.registerForm.get('businessfield')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
    this.filteredOptions = this.businesses.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }
  
  /**
   * Handles business selection from dropdown
   * 
   * Processes business selection and initiates farm site loading:
   * 1. Sets selected business name in form field
   * 2. Closes dropdown menu
   * 3. Stores business name for display
   * 4. Sets business model ID
   * 5. Loads farm sites for selected business
   * 6. Resets farm site and section models
   * 
   * Flow:
   * - User selects business from dropdown
   * - Business name is displayed in form field
   * - Dropdown closes automatically
   * - Farm sites are loaded asynchronously
   * - If only one farm site exists, it's auto-selected
   * 
   * Post-selection state:
   * - businessModel.id: Set to selected business ID
   * - registerForm.businessfield: Displays business name
   * - businessSearch: Updated with business name
   * - loginBusinessName: Synced with selection
   * - farmsites: Populated with farm sites
   * - sections: Cleared for next selection
   * 
   * @async
   * @method selectOption
   * @param {BusinessEntity} business - The selected business entity
   * @returns {Promise<void>}
   * @side-effect Updates form field, loads farm sites, updates UI state
   * 
   * @example
   * this.selectOption(businessEntity); // Called from dropdown selection
   */
  public async selectOption(business: BusinessEntity) {
    this.registerForm.get('businessfield')!.setValue(business.businessName);
    this.isDropdownOpen = false;
    this.businessSearch = business.businessName;
    this.loginBusinessName = business.businessName;
    this.businessModel.id = business.id;
    if (this.businessModel.id) {
      this.getFarmSitesByBusinessID(business.id);
    }
  }

  /**
   * Retrieves and loads farm sites for selected business
   * 
   * Fetches all farm sites associated with a business ID and updates the UI.
   * Implements auto-selection if only one farm site is available.
   * 
   * Process flow:
   * 1. Clears previous farm sites and sections
   * 2. Resets farm site and section model IDs
   * 3. Calls FarmSiteService to get farm sites
   * 4. Auto-selects farm site if exactly one is available
   * 5. Loads sections for auto-selected farm site
   * 6. Handles errors gracefully
   * 
   * Auto-selection logic:
   * - If farmsites.length === 1, set sectionModel.id and load sections
   * - Else, require user to manually select farm site
   * 
   * Error handling:
   * - Catches service errors
   * - Displays user-friendly error messages
   * - Clears data arrays on failure
   * 
   * localStorage key used:
   * - businessID: For direct farm access (Farmers/Farm Managers)
   * 
   * @async
   * @method getFarmSitesByBusinessID
   * @param {string} businessId - The business identifier
   * @returns {Promise<void>}
   * @throws {Error} If FarmSiteService call fails
   * @side-effect Updates farmsites, sections, UI state
   * 
   * @example
   * await this.getFarmSitesByBusinessID('60f4c8b5e5c8b5');
   */
  public async getFarmSitesByBusinessID(businessId: string): Promise<void> {
    try {
      this.farmsites = [];
      this.sections = [];
      this.farmsiteModel.id = undefined;
      this.sectionModel.id = undefined;
      this.farmsites = await this.farmSiteService.getFarmeSitesByFarmId(businessId);
      if (this.farmsites && this.farmsites.length == 1) {
        this.farmsiteModel.id = this.farmsites[0].id;
        this.getSectionsByFarmSiteID(this.farmsiteModel.id);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Retrieves and loads pond sections for selected farm site
   * 
   * Fetches all pond sections associated with a farm site ID.
   * Implements auto-selection if only one section is available.
   * 
   * Process flow:
   * 1. Clears previous sections
   * 2. Resets section model ID
   * 3. Calls PondSectionService to get sections
   * 4. Auto-selects section if exactly one is available
   * 5. Handles errors gracefully
   * 
   * Auto-selection logic:
   * - If sections.length === 1, set sectionModel.id
   * - Else, require user to manually select section
   * 
   * Error handling:
   * - Catches service errors
   * - Displays user-friendly error messages
   * - Continues operation on partial failures
   * 
   * @async
   * @method getSectionsByFarmSiteID
   * @param {string} farmSiteID - The farm site identifier
   * @returns {Promise<void>}
   * @throws {Error} If PondSectionService call fails
   * @side-effect Updates sections, sectionModel, UI state
   * 
   * @example
   * await this.getSectionsByFarmSiteID('60f4c8b5farm');
   */
  public async getSectionsByFarmSiteID(farmSiteID: string): Promise<void> {
    try {
      this.sections = [];
      this.sectionModel.id = undefined;
      this.sections = await this.sectionService.getSectionsByFarmsiteId(farmSiteID);
      if (this.sections && this.sections.length == 1) {
        this.sectionModel.id = this.sections[0].id;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Getter method to access form controls
   * 
   * Provides convenient access to form controls in templates using the syntax f.controlName.
   * Used for validation error display and field state checking.
   * 
   * Available controls:
   * - f.businessfield: Business selection field
   * - f.locationField: Farm site selection field
   * - f.sectionField: Pond section selection field
   * 
   * @method f
   * @returns {any} - The form controls object with all form field references
   * 
   * @example
   * // In template:
   * <div *ngIf="f.businessfield?.invalid && (f.businessfield?.dirty || f.businessfield?.touched)">
   *   Business is required
   * </div>
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * Handles form submission and culture estimation calculation
   * 
   * Processes form submission to calculate culture estimations for selected section.
   * Performs validation, API call, and result display.
   * 
   * Submission flow:
   * 1. Marks all form fields as touched for validation display
   * 2. Validates form structure
   * 3. Returns early if form is invalid
   * 4. Resets submitted flag
   * 5. Enables loading indicator and disables submit button
   * 6. Constructs request body with selected values
   * 7. Calls CultureEstimatorService to get estimations
   * 8. Stores results in dupCultureEstimatorsData
   * 9. Sets isSubmitClicked flag to show results
   * 10. Handles errors gracefully
   * 
   * Request body contains:
   * - farm_site_id: Selected farm site ID
   * - section_id: Selected section ID
   * - farmNameAtLocation: Farm display name
   * - personName: Current user name from localStorage
   * - mobileNumber: Current user mobile number
   * - businessName: Selected business name
   * 
   * Validation:
   * - businessfield: Required
   * - locationField: Required
   * - sectionField: Required
   * 
   * UI state during submission:
   * - Loading spinner shown
   * - Submit button disabled
   * - Results hidden until data loaded
   * 
   * Data display:
   * - Sets isSubmitClicked = true to show results
   * - Stores data in dupCultureEstimatorsData
   * - Both current and estimated data included
   * 
   * @async
   * @method onSubmit
   * @returns {Promise<void>}
   * @throws {Error} If CultureEstimatorService call fails
   * @side-effect Updates form state, loads culture data, updates UI
   * 
   * @example
   * // Called from form submission
   * <form (ngSubmit)="onSubmit()">
   *   <button type="submit">Calculate Estimation</button>
   * </form>
   */
  public async onSubmit() {
    this.formSubmitted = true;
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid) {
      return;
    }
    this.formSubmitted = false;
    try {
      this.submitButtonDisabled = true;
      this.enableDisableDataLoader = true;
      this.isSubmitClicked = false;
      const reqBody = {
        "farm_site_id": this.farmsiteModel.id,
        "section_id": String(this.sectionModel.id),
        "farmNameAtLocation": this.farmsites.find((obj: FarmSiteModel) => obj.id == this.farmsiteModel.id).farmNameAtLocation,
        "personName": localStorage.getItem('LoginUserName'),
        "mobileNumber": localStorage.getItem('loginPersonMobileNumber'),
        "businessName": this.loginBusinessCategory != 'Provider' ? localStorage.getItem('loginBusinessName') : this.loginBusinessName
      }
      this.cultureEstimatorsData = await this.cultureEstimatorService.getCurrentAndEstimatedDataBySection(reqBody);
      if (this.cultureEstimatorsData) {
        if (this.cultureEstimatorsData.Data.length) {
          this.dupCultureEstimatorsData = this.cultureEstimatorsData;
        }
      }
      this.enableDisableDataLoader = false; 
      this.submitButtonDisabled = false;
      this.isSubmitClicked = true;
    } catch(error) {
      this.enableDisableDataLoader = false;
      this.submitButtonDisabled = false;
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Opens the culture estimation view in fullscreen mode
   * 
   * Displays the culture estimator results in fullscreen using the browser's
   * Fullscreen API. Requires element with id 'zoomView' to exist in template.
   * 
   * Fullscreen flow:
   * 1. Gets DOM element with id 'zoomView'
   * 2. Checks if browser supports requestFullscreen
   * 3. Initiates fullscreen mode if supported
   * 
   * Browser compatibility:
   * - Standard API: element.requestFullscreen()
   * - Webkit (Safari): element.webkitRequestFullscreen()
   * - Mozilla (Firefox): element.mozRequestFullScreen()
   * - MS (IE): element.msRequestFullscreen()
   * 
   * Supported browsers:
   * - Chrome, Edge, Firefox, Safari (recent versions)
   * - Mobile browsers with fullscreen support
   * 
   * Exit fullscreen:
   * - Press ESC key
   * - User clicks exit button (implementation dependent)
   * - Browser automatically exits on navigation
   * 
   * Use cases:
   * - Detailed analysis of culture parameters
   * - Chart/graph visualization
   * - Presentation mode for reports
   * 
   * @method openFullScreen
   * @returns {void}
   * @side-effect Requests fullscreen mode from browser
   * 
   * @example
   * this.openFullScreen(); // Called from view button
   */
   public openFullScreen(): void {
    let element = document.getElementById('zoomView') as HTMLElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    }
  }

  /**
   * Exports culture estimation data to Excel file
   * 
   * Downloads the calculated culture estimation results as an Excel file
   * from the backend server. Constructs download URL and opens in new window.
   * 
   * Export process:
   * 1. Gets Excel file name from cultureEstimatorsData
   * 2. Constructs full download URL from environment config
   * 3. Opens file in new window for download
   * 4. Uses '_self' target for same-window download
   * 
   * URL construction:
   * - Base: environment.newPythonBaseUrl
   * - Path: /download_Excel_File/{fileName}
   * - Full URL: environment.newPythonBaseUrl/download_Excel_File/{fileName}
   * 
   * File naming:
   * - File name provided by backend service
   * - Typically includes: farm_site, section, timestamp
   * - Example: CultureEstimator_FarmA_Section1_2024-01-19.xlsx
   * 
   * Download behavior:
   * - Browser handles download automatically
   * - File saved to default downloads folder
   * - Opens in current window (can be changed with target)
   * 
   * Requirements:
   * - cultureEstimatorsData must be loaded
   * - cultureEstimatorsData.FileName must exist
   * - Backend file must be accessible
   * - CORS headers must allow download
   * 
   * Error handling:
   * - Catches download errors
   * - Displays user-friendly error messages
   * 
   * @method exportExcel
   * @returns {void}
   * @throws {Error} If download URL construction fails
   * @side-effect Triggers file download
   * 
   * @example
   * this.exportExcel(); // Called from export button
   */
  public exportExcel() {
    try {
      let excelFileName = environment.newPythonBaseUrl + '/download_Excel_File/' + this.cultureEstimatorsData.FileName;
      window.open(excelFileName, '_self');
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Angular lifecycle hook - Component destruction
   * 
   * Called once when the component is being destroyed.
   * Cancels all ongoing HTTP requests to prevent memory leaks and
   * unnecessary backend operations.
   * 
   * Cleanup operations:
   * - Cancels BusinessEntityService requests
   * - Cancels FarmSiteService requests
   * - Cancels PondSectionService requests
   * - Cancels CultureEstimatorService requests
   * - Cancels PersonsService requests
   * 
   * Importance:
   * - Prevents memory leaks from pending HTTP requests
   * - Reduces unnecessary server load
   * - Improves application performance
   * - Essential for proper Angular component cleanup
   * 
   * When called:
   * - Component is removed from DOM
   * - Route change occurs
   * - Application shutdown
   * 
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook - Called once before component is destroyed
   */
  public ngOnDestroy(): void {
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.farmSiteService.cancelOnGoingHttpRequests();
    this.sectionService.cancelOnGoingHttpRequests();
    this.cultureEstimatorService.cancelOnGoingHttpRequests();
    this.personService.cancelOnGoingHttpRequests();
  }
}
